import {
  Paper,
  Typography,
  Divider,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
  Container, // Use Container for better centering and max-width
  useTheme, // Access theme for consistent styling
  Fade, // Add subtle animation
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"; // Icon for Help
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"; // Icon for Info/Empty state
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"; // Icon for Error state

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ToastApp from "../../ToastApp"; // Assuming this is correctly configured
import HOST_URL from "../../utils/Url";
import axios from "axios";


type Props = {};

export default function HelpDesk(props: Props) {
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme(); // Access theme

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pageName, setPageName] = useState<string | null>(null);
  const [pageDesk, setPageDesk] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state on component mount or relevant dependency change
    setIsLoading(true);
    setError(null);
    setPageName(null);
    setPageDesk(null);

  
     const menuId = localStorage.getItem("menuData");

    console.log("Attempting to fetch help for menuId:", menuId);

    if (!menuId) {
      const errorMsg = "No menu id provided";
      setError(errorMsg);
      toast.error(errorMsg);
      setIsLoading(false);
      return; // Stop execution if no menuId
    }

    fetchHelpContent(menuId);

    // Cleanup function if needed (e.g., aborting fetch requests)
    // return () => {
    //   // Cleanup logic here
    // };

  }, [location.pathname, t]); // Rerun if path changes or language changes

  const fetchHelpContent = async (id: string) => {
    try {
      const collectData = { pageTitleId: id };
      // Replace mockApi.post with your actual api.post call
      const res = await  axios.post(HOST_URL.HOST_URL2 +`HelpCreation/GetHelpCreation`, collectData);
      // const res = await api.post(`HelpCreation/GetHelpCreation`, collectData);

      if (res.data && res.data.data && res.data.data.length > 0) {
        const helpData = res.data.data[0];
        setPageName(helpData["page_Name"] ?? t("Default Page Name")); // Fallback title

        setPageDesk(helpData["frontDesign"] ?? null); // Can be null if field exists but is empty
        if (!helpData["frontDesign"]) {
           console.warn("Help content received but 'frontDesign' is empty.");
           // Optional: Treat empty content as "not found"
           // setError(t("helpdesk.info.noContentFound"));
        }
      } else {
        // API call succeeded but returned no data for this ID
        console.warn("No help content found for menuId:", id);
        setError("No content found"); // Set an informative message
      }
    } catch (err: any) {
      console.error("Error fetching help content:", err);
      const errorMsg = err.message || "An error occurred while fetching help content.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false); // Ensure loading is set to false in all cases
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: "300px" }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading...</Typography>
        </Box>
      );
    }

    if (error) {
      // Differentiate between "not found" info and actual errors
      const isNotFoundError = error === "No content found";
      return (
        <Alert
          severity={isNotFoundError ? "info" : "error"}
          iconMapping={{
            info: <InfoOutlinedIcon fontSize="inherit" />,
            error: <ErrorOutlineIcon fontSize="inherit" />,
          }}
          sx={{ mt: 2 }}
        >
          <AlertTitle>{isNotFoundError ? "Error" : "Error"}</AlertTitle>

          {error}
        </Alert>
      );
    }

    if (pageName && pageDesk) {
      return (
        <Fade in={true} timeout={500}>
            <Box>
                {/* Header Section */}
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <HelpOutlineIcon color="primary" sx={{ fontSize: '2rem' }} />
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'medium' }}>
                        {pageName}
                    </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Content Section */}
                <Box
                    className="help-content ql-snow" // Add quill styles if content comes from Quill editor
                    sx={{
                        mt: 2,
                        // Basic styling for content readability
                        lineHeight: 1.7,
                        '& h1, & h2, & h3': { // Style headers within the content
                           marginTop: theme.spacing(3),
                           marginBottom: theme.spacing(1.5),
                           fontWeight: 'medium',
                           color: theme.palette.primary.dark, // Example: Darker primary color for headers
                        },
                        '& p': { // Style paragraphs
                            marginBottom: theme.spacing(2),
                        },
                        '& ul, & ol': { // Style lists
                            marginBottom: theme.spacing(2),
                            paddingLeft: theme.spacing(4), // Indent lists
                        },
                        '& li': { // Style list items
                            marginBottom: theme.spacing(1),
                        },
                        '& strong': { // Make bold text slightly more prominent
                           fontWeight: 'bold',
                        },
                        '& a': { // Style links
                           color: theme.palette.primary.main,
                           textDecoration: 'none',
                           '&:hover': {
                                textDecoration: 'underline',
                           }
                        },
                        '& pre': { // Style code blocks
                            backgroundColor: theme.palette.grey[100],
                            border: `1px solid ${theme.palette.grey[300]}`,
                            borderRadius: theme.shape.borderRadius,
                            padding: theme.spacing(2),
                            overflowX: 'auto', // Allow horizontal scrolling for long lines
                            marginBottom: theme.spacing(2),
                            fontSize: '0.9rem', // Slightly smaller font for code
                        },
                        '& code': { // Style inline code
                           fontFamily: 'monospace', // Use monospace font
                           backgroundColor: theme.palette.grey[200],
                           padding: '0.1em 0.4em',
                           borderRadius: '4px',
                        }
                    }}
                >
                   {/* Render HTML safely */}
                   <Typography component="div" dangerouslySetInnerHTML={{ __html: pageDesk }} />
                </Box>
            </Box>
        </Fade>
      );
    }

    // Fallback if no error, not loading, but also no content (should be caught by error state usually)
    return (
      <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />} sx={{ mt: 2 }}>
        <AlertTitle>Information</AlertTitle>
        No help content found for this menu.
      </Alert>
    );
  };

  return (
    <>
      {/* Container centers content and provides max-width */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
         <Paper
            elevation={3} // Add some shadow
            sx={{
              p: { xs: 2, sm: 3, md: 4 }, // Responsive padding
              overflow: "hidden", // Ensure content doesn't overflow paper
              backgroundColor: theme.palette.background.paper, // Use theme background
            }}
         >
          {renderContent()}
        </Paper>
      </Container>
      {/* Keep ToastApp if it's used for global notifications */}
      <ToastApp />
      {/* ConfirmDialog is imported but not used in the provided snippet. Remove if not needed */}
      {/* <ConfirmDialog />  */}
    </>
  );
}

// Add corresponding i18n keys to your translation files (e.g., en.json):
/*
{
  "helpdesk": {
    "loading": "Loading Help Content",
    "defaultTitle": "Help Information",
    "error": {
      "title": "Error",
      "noMenuId": "Cannot display help: Missing page identifier.",
      "fetchFailed": "Failed to load help content. Please try again later.",
    },
    "info": {
        "title": "Information",
        "noContentFound": "No specific help content found for this page.",
        "noContentAvailable": "Help content is currently unavailable."
    }
  }
}
*/